import React, {useContext, useEffect, useState} from "react";
import {Box, CircularProgress, colors, makeStyles, styled, Switch, TableRow, Typography} from "@material-ui/core";
import {FormattedMessage, useIntl} from "react-intl";
import moment from "moment";
import voca from "voca";

import {APP_NAME} from "../../settings/constants";
import {
  LanguageActionTypeEnum,
  LanguageContext,
  LanguageDispatchContext,
} from "../../context/LanguageContext";
import {supportedLanguages} from "../../components/language/Language";
import {DefaultButton} from "../icip_admin_screen/components/Buttons";
import {PageTitle} from "../../components/page-title/PageTitle";
import RaffleDrawService from "../../services/RaffleDrawService";
import ExpandedContent from "../icip_admin_screen/components/ExpandedContent";
import TableData, {StyledTableCell} from "../icip_admin_screen/components/TableData";
import {formatPhoneNumber} from "../../common/Hooks";
import {Helmet} from "react-helmet";

export const useStyles = makeStyles(
    (theme) => ({
      root: {
        height: "100%",
        overflowX: "hidden",
        overflowY: "auto",
        backgroundColor: "#FAFAFA",
        paddingBottom: "32px",

        "& table": {
          overflow: "hidden",

          "& thead tr th:first-child": {
            padding: "14px 32px 14px 34px",
            textAlign: "left",

            [theme.breakpoints.down('md')]: {
              width: "50px",
            },
          },
          "& thead tr th": {
            padding: "14px 32px 14px 12px",
            textAlign: "left",
            fontSize: "1rem !important",
            borderBottom: "1px solid #e8e8e8",
            // borderTop: "1px solid #e8e8e8",
          },
          "& thead tr th:last-child": {
            textAlign: "right",
            [theme.breakpoints.down('md')]: {
              textAlign: "left"
            },
          },
          "& tbody tr td:last-child": {
            textAlign: "right",
            [theme.breakpoints.down('md')]: {
              textAlign: "left",
            },
          },
          "& tbody tr td": {
            padding: "14px 32px 14px 12px",
            textAlign: "left",
            cursor: "pointer",
            border: "none",

            [theme.breakpoints.down('md')]: {
              verticalAlign: "top",
            },
          },
          "& tbody tr td:first-child": {
            padding: "14px 32px 14px 34px",
            fontWeight: 700,
          },
        },
      },
      header: {
        display: "flex",
        alignItems: "center",
        padding: "20px 20px 2px 32px",

        [theme.breakpoints.down('md')]: {
          display: "block",
          textAlign: "center",
        },
      },
      mainTitle: {
        fontWeight: theme.typography.fontWeightBold,
        fontSize: "1.4rem",
        color: "#F94F4F",
        [theme.breakpoints.down('md')]: {
          fontSize: "1.2rem",
        },
      },
      secondaryTitle: {
        fontWeight: theme.typography.fontWeightBold,
        fontSize: "1.4rem",
        color: theme.palette.secondary.main,
        [theme.breakpoints.down('md')]: {
          fontSize: "1.2rem",
        },
      },
      subTitle: {
        fontWeight: theme.typography.fontWeightMedium,
        fontSize: "1rem",
        color: theme.palette.text.primary,
        [theme.breakpoints.down('md')]: {
          display: "block",
          marginBottom: "20px"
        },
      },
      languageWrapper: {
        display: "flex",
        alignItems: "center",

        [theme.breakpoints.down('md')]: {
          justifyContent: "center",
          marginTop: "14px"
        },
      },
      languageItem: {
        padding: "2px 14px 2px 8px",
        fontSize: "0.88rem",
        cursor: "pointer"
      },
      loadingWrapper: {
        display: "flex",
        alignItems: "center",
        padding: "0 34px",

        [theme.breakpoints.down('md')]: {
          justifyContent: "center",
        },

        "& .caption": {
          color: theme.palette.grey[600],
          fontSize: "0.88rem",
        },
      },
      specialCell: {
        [theme.breakpoints.down('md')]: {
          minWidth: "300px",
        },
      },
      specialTelCell: {
        width: "100%",
        textAlign: "right",
      },
      genericCell: {
        [theme.breakpoints.down('md')]: {
          width: "100px",
        },
      },
      errorWrapper: {
        marginTop: "20px",
        padding: "0 32px",
        fontSize: "0.84rem",
        color: colors.red[800],

        [theme.breakpoints.down('md')]: {
          textAlign: "center"
        },
      },
      resultCard: {
        backgroundColor: colors.common.white,
        margin: "0 32px",
        borderRadius: "4px",
        border: "1px solid #e8e8e8",

        [theme.breakpoints.down('md')]: {
          margin: "0 0",
        },
      }
    }),
    {
      name: "public-raffle-winners-list-style",
      flip: null,
    }
);

export const CustomSwitch = styled(Switch)(({ theme }) => ({
  width: 34,
  height: 15,
  padding: 0,
  display: 'flex',
  '&:active': {
    '& .MuiSwitch-thumb': {
      width: 15,
    },
    '& .MuiSwitch-switchBase.Mui-checked': {
      transform: 'translateX(14px)',
    },
  },
  '& .MuiSwitch-switchBase': {
    padding: 2,
    '&.Mui-checked': {
      transform: 'translateX(18px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        opacity: 1,
        backgroundColor: '#F94F4F',
      },
    },
  },
  '& .MuiSwitch-thumb': {
    boxShadow: '0 2px 4px 0 rgb(0 35 11 / 20%)',
    width: 12,
    height: 10,
    borderRadius: 6,
    transition: theme.transitions.create(['width'], {
      duration: 200,
    }),
  },
  '& .MuiSwitch-track': {
    borderRadius: 16 / 2,
    opacity: 1,
    backgroundColor: "rgba(0,0,0,.25)",
    boxSizing: 'border-box',
  },
}));

enum LoadingStatus {
  loading,
  failure,
  succeed,
  initial,
}

interface PublicRaffleDrawWinnersProps {}

export const PublicRaffleDrawWinners = (
  props: PublicRaffleDrawWinnersProps
) => {
  const classes = useStyles(props);
  const intl = useIntl();
  // Context
  const languageState = useContext(LanguageContext);
  // State
  const [loadingStatus, setLoadingStatus] = useState(
      (): LoadingStatus => LoadingStatus.initial
  );
  const [raffleWinnersTable, setRaffleWinnersTable] = useState([]);

  const fetchWinners = () => {
    setLoadingStatus(LoadingStatus.loading);

    RaffleDrawService.getAllRaffleDrawsWinners().then((response) => {
      if (response.isSuccess && response.data.code === 100) {
        const winnerResultTable = Object.values(
            JSON.parse(JSON.stringify(response.data.data))
        );
        setRaffleWinnersTable(winnerResultTable);
        setLoadingStatus(LoadingStatus.succeed);
      } else {
        setLoadingStatus(LoadingStatus.failure);
      }
    });
  };

  const renderCommonHeaders = () => {
    return (
        <TableRow>
          <StyledTableCell align="left">#.</StyledTableCell>
          <StyledTableCell align="left">
            <Typography component="span" noWrap>
              <FormattedMessage id="sharedScreen.lastName" />
            </Typography>
          </StyledTableCell>
          <StyledTableCell align="left">
            <Typography component="span" noWrap>
              <FormattedMessage id="sharedScreen.firstName" />
            </Typography>
          </StyledTableCell>
          <StyledTableCell align="left">
            <Typography component="span" noWrap>
              <FormattedMessage id="sharedScreen.packageWon" />
            </Typography>
          </StyledTableCell>
          <StyledTableCell align="right">
            <Typography component="span" noWrap>
              <FormattedMessage id="sharedScreen.phoneNumber" />
            </Typography>
          </StyledTableCell>
        </TableRow>
    );
  };

  const renderCommonRows = (raffleDrawWinners: Array<any>) => {
    return raffleDrawWinners.map((winner, index) => {
      return (
          <TableRow>
            <StyledTableCell align="left">
              <Typography component="span">
                {index + 1}
              </Typography>
            </StyledTableCell>
            <StyledTableCell align="left">
              <Typography component="span" noWrap>
                {voca.titleCase(winner['participant'].last_name)}
              </Typography>
            </StyledTableCell>
            <StyledTableCell align="left">
              <Typography component="span" noWrap>
                {voca.titleCase(winner['participant'].first_name)}
              </Typography>
            </StyledTableCell>
            <StyledTableCell align="left">
              <Box component="div" className={classes.specialCell} style={{fontWeight: "bold"}}>
                {
                  languageState.localeCode === "fr" ? (
                      winner.winning_package.designation_fr
                  ) : (
                      winner.winning_package.designation_en
                  )
                }
              </Box>
            </StyledTableCell>
            <StyledTableCell align="right">
              <Box component="div" className={classes.specialTelCell} style={{fontWeight: "bold"}}>
                <Typography component="span" style={{fontWeight: "bold"}} noWrap>
                  {formatPhoneNumber(winner['participant'].telephone)}
                </Typography>
              </Box>
            </StyledTableCell>
          </TableRow>
      );
    })
  };

  const errorWrapper = (error?: string): any => {
    return (
        <Box component="div" className={classes.errorWrapper}>
          <Typography component="span">
            {error ? (
                error
            ) : (
                <FormattedMessage id="sharedScreen.unableToLoadData" />
            )}
          </Typography>
        </Box>
    );
  };

  const renderSingleRaffleDrawWinners = (raffleDrawWinners: any) => {
    let title = (<></>);
    let raffleDate = moment.utc(raffleDrawWinners[0].raffle_draw.raffle_draw_date).local().format("DD, MMM yyyy @HH:mm");

    if(raffleDrawWinners[0].raffle_draw.raffle_draw_type === 1) {
      title = (
          <FormattedMessage
              id="sharedScreen.raffleDrawOfWithDate"
              values={{ date: raffleDate, type: intl.formatMessage({ id: "sharedScreen.televised" }) }}
          />
      )
    } else {
      title = (
          <FormattedMessage
              id="sharedScreen.raffleDrawOfWithDate"
              values={{ date: raffleDate, type: intl.formatMessage({ id: "sharedScreen.special" }) }}
          />
      )
    }

    return (
        <Box component="div" className={classes.resultCard} boxShadow={0}>
          <ExpandedContent title={title} withBorder={false} defaultExpanded={true} withPadding>
            <Box component="div">
              <TableData
                  header={renderCommonHeaders()}
                  rows={renderCommonRows(raffleDrawWinners)}
              />
            </Box>
          </ExpandedContent>
        </Box>
    );
  };

  useEffect(() => {
    fetchWinners();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
      <>
        <Helmet>
          <meta name="viewport" content="width=device-width, initial-scale=1" />
        </Helmet>
        <PageTitle
            withAppTitle
            title={intl.formatMessage({ id: "sharedScreen.raffleDrawWinner" })}
        />
        <Box className={classes.root}>
          <Box className={classes.header}>
            <Box style={{flexGrow: 1}}>
              <Box component="div">
                <Typography component="span" className={classes.secondaryTitle}>
                  <FormattedMessage id="sharedScreen.specialRaffle" />
                </Typography>
                <Typography component="span" className={classes.mainTitle}>
                  {` ${APP_NAME} `}
                </Typography>
                <Typography component="span" className={classes.secondaryTitle}>
                  ⸱ Qatar 2022
                </Typography>
              </Box>
              <Typography component="span" className={classes.subTitle}>
                <FormattedMessage id="sharedScreen.winnersRaffleList" />
              </Typography>
            </Box>
            <Box mr={2}>
              <DefaultButton
                  component="div"
                  onClick={(_) => loadingStatus !== LoadingStatus.loading ? fetchWinners() : {}}
              >
                <FormattedMessage id="sharedScreen.refresh" />
              </DefaultButton>
            </Box>
            <SwitchLanguageWrapper />
          </Box>
          {
            loadingStatus === LoadingStatus.failure ? (
                errorWrapper()
            ) : (
                loadingStatus === LoadingStatus.loading ? (
                    <Box component="div" mt={2} className={classes.loadingWrapper}>
                      <Box component="div" mr={2} mt={1}>
                        <CircularProgress
                            color="secondary"
                            disableShrink={false}
                            size={18}
                            thickness={4}
                        />
                      </Box>
                      <Typography component="span" className="caption">
                        <FormattedMessage id="sharedScreen.loadingData" />
                      </Typography>
                    </Box>
                ) : <></>
            )
          }
          {
            loadingStatus === LoadingStatus.succeed ? (
                raffleWinnersTable.map((raffleDrawWinners, index) => {
                  return (
                      <Box mt={4} key={index}>
                        {renderSingleRaffleDrawWinners(raffleDrawWinners)}
                      </Box>
                  );
                })
            ) : <></>
          }
        </Box>
      </>
  );
};

export const useLanguageWrapperStyles = makeStyles(
    (theme) => ({
      languageWrapper: {
        display: "flex",
        alignItems: "center",

        [theme.breakpoints.down('md')]: {
          justifyContent: "center",
          marginTop: "14px"
        },
      },
      languageItem: {
        padding: "2px 14px 2px 12px",
        fontSize: (props: SwitchLanguageWrapperProps) => props.size === "small" ? "0.8rem" : "0.88rem",
        cursor: "pointer"
      },
    }),
    {
      name: "switch-language-wrapper-style",
      flip: null,
    }
);

interface SwitchLanguageWrapperProps {
  size?: "small" | "default"
}

export const SwitchLanguageWrapper = (props: SwitchLanguageWrapperProps) => {
  const classes = useLanguageWrapperStyles(props);
  // Context
  const languageState = useContext(LanguageContext);
  const languageDispatch = useContext(LanguageDispatchContext);
  // State
  const [switched, setSwitched] = React.useState(languageState.localeCode === "fr");

  const handleLanguageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    changeLanguage(event.target.checked);
  };

  const changeLanguage = (value: boolean) => {
    setSwitched(value);
    languageDispatch({
      type: LanguageActionTypeEnum.switch,
      data: value ? supportedLanguages[0] : supportedLanguages[1],
    });
  };

  return (
      <>
        <Box component="div" className={classes.languageWrapper}>
          <Typography
              onClick={(_) => changeLanguage(false)}
              component="span"
              className={classes.languageItem}
              style={{color: switched ? colors.grey[600] : '#263238', fontWeight: !switched ? "bold" : 500}}>
            <FormattedMessage id="sharedScreen.english" />
          </Typography>
          <CustomSwitch
              checked={switched}
              onChange={handleLanguageChange}
              defaultChecked
              inputProps={{ 'aria-label': 'language wrapper' }}
          />
          <Typography
              onClick={(_) => changeLanguage(true)}
              component="span"
              className={classes.languageItem}
              style={{color: !switched ? colors.grey[600] : '#263238', fontWeight: switched ? "bold" : 500}}>
            <FormattedMessage id="sharedScreen.french" />
          </Typography>
        </Box>
      </>
  );
};

SwitchLanguageWrapper.defaultProps = {
  size: "default"
};

PublicRaffleDrawWinners.routeName = "/public/raffle-draw/winners";
